import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import styles from '../styles/Settings.module.scss';

interface UserPreferences {
  theme: 'light' | 'dark' | 'auto';
  notifications: {
    email: boolean;
    push: boolean;
    emergency: boolean;
  };
  privacy: {
    shareLocation: boolean;
    publicProfile: boolean;
  };
  accessibility: {
    reducedMotion: boolean;
    highContrast: boolean;
    fontSize: 'small' | 'medium' | 'large';
  };
}

const Settings: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>({
    theme: 'dark',
    notifications: {
      email: true,
      push: true,
      emergency: true
    },
    privacy: {
      shareLocation: false,
      publicProfile: false
    },
    accessibility: {
      reducedMotion: false,
      highContrast: false,
      fontSize: 'medium'
    }
  });
  const [isSaving, setIsSaving] = useState(false);

  // Load preferences from localStorage on component mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem('oceanGuardPreferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Error parsing saved preferences:', error);
      }
    }
  }, []);

  // Apply theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (preferences.theme === 'light') {
      root.setAttribute('data-theme', 'light');
    } else if (preferences.theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      // Auto theme based on system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    }
  }, [preferences.theme]);

  const handlePreferenceChange = (section: keyof UserPreferences, key: string, value: any) => {
    setPreferences(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as object),
        [key]: value
      }
    }));
  };

  const handleThemeChange = (theme: 'light' | 'dark' | 'auto') => {
    setPreferences(prev => ({
      ...prev,
      theme
    }));
  };

  const savePreferences = async () => {
    setIsSaving(true);
    try {
      // Save to localStorage
      localStorage.setItem('oceanGuardPreferences', JSON.stringify(preferences));
      
      // Here you could also save to Firestore if needed
      // await updateDoc(doc(db, 'userPreferences', currentUser.uid), preferences);
      
      toast.success('Settings saved successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save settings. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error) {
      toast.error('Error logging out. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  return (
    <motion.div
      className={styles.settingsPage}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className={styles.container}>
        {/* Header */}
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h1 className={styles.title}>
            <span className={styles.titleHighlight}>Settings</span>
          </h1>
          <p className={styles.subtitle}>
            Customize your OceanGuard experience and manage your preferences.
          </p>
        </motion.div>

        <div className={styles.settingsGrid}>
          {/* User Profile Section */}
          <motion.div
            className={styles.settingsCard}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
              </div>
              <h2 className={styles.cardTitle}>Profile</h2>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.profileInfo}>
                <div className={styles.avatar}>
                  {currentUser?.photoURL ? (
                    <img src={currentUser.photoURL} alt="Profile" />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                    </div>
                  )}
                </div>
                <div className={styles.userInfo}>
                  <h3>{currentUser?.displayName || 'User'}</h3>
                  <p>{currentUser?.email}</p>
                </div>
              </div>
              <button className={styles.logoutButton} onClick={handleLogout}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                </svg>
                Logout
              </button>
            </div>
          </motion.div>

          {/* Theme Settings */}
          <motion.div
            className={styles.settingsCard}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z"/>
                </svg>
              </div>
              <h2 className={styles.cardTitle}>Appearance</h2>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.settingGroup}>
                <label className={styles.settingLabel}>Theme</label>
                <div className={styles.themeOptions}>
                  {(['light', 'dark', 'auto'] as const).map((theme) => (
                    <button
                      key={theme}
                      className={`${styles.themeOption} ${preferences.theme === theme ? styles.active : ''}`}
                      onClick={() => handleThemeChange(theme)}
                    >
                      <div className={styles.themePreview}>
                        {theme === 'light' && (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="12" r="5"/>
                            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                          </svg>
                        )}
                        {theme === 'dark' && (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
                          </svg>
                        )}
                        {theme === 'auto' && (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0z"/>
                            <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" opacity="0.5"/>
                          </svg>
                        )}
                      </div>
                      <span>{theme.charAt(0).toUpperCase() + theme.slice(1)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Notifications Settings */}
          <motion.div
            className={styles.settingsCard}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                </svg>
              </div>
              <h2 className={styles.cardTitle}>Notifications</h2>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.settingGroup}>
                <div className={styles.toggleSetting}>
                  <div className={styles.toggleInfo}>
                    <label className={styles.settingLabel}>Email Notifications</label>
                    <p className={styles.settingDescription}>Receive updates via email</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={preferences.notifications.email}
                      onChange={(e) => handlePreferenceChange('notifications', 'email', e.target.checked)}
                    />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>

                <div className={styles.toggleSetting}>
                  <div className={styles.toggleInfo}>
                    <label className={styles.settingLabel}>Push Notifications</label>
                    <p className={styles.settingDescription}>Browser notifications for updates</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={preferences.notifications.push}
                      onChange={(e) => handlePreferenceChange('notifications', 'push', e.target.checked)}
                    />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>

                <div className={styles.toggleSetting}>
                  <div className={styles.toggleInfo}>
                    <label className={styles.settingLabel}>Emergency Alerts</label>
                    <p className={styles.settingDescription}>Critical ocean safety alerts</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={preferences.notifications.emergency}
                      onChange={(e) => handlePreferenceChange('notifications', 'emergency', e.target.checked)}
                    />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Privacy Settings */}
          <motion.div
            className={styles.settingsCard}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z"/>
                </svg>
              </div>
              <h2 className={styles.cardTitle}>Privacy</h2>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.settingGroup}>
                <div className={styles.toggleSetting}>
                  <div className={styles.toggleInfo}>
                    <label className={styles.settingLabel}>Share Location</label>
                    <p className={styles.settingDescription}>Allow location sharing for better recommendations</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={preferences.privacy.shareLocation}
                      onChange={(e) => handlePreferenceChange('privacy', 'shareLocation', e.target.checked)}
                    />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>

                <div className={styles.toggleSetting}>
                  <div className={styles.toggleInfo}>
                    <label className={styles.settingLabel}>Public Profile</label>
                    <p className={styles.settingDescription}>Make your profile visible to other users</p>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={preferences.privacy.publicProfile}
                      onChange={(e) => handlePreferenceChange('privacy', 'publicProfile', e.target.checked)}
                    />
                    <span className={styles.toggleSlider}></span>
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Save Button */}
        <motion.div
          className={styles.saveSection}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <button
            className={styles.saveButton}
            onClick={savePreferences}
            disabled={isSaving}
          >
            {isSaving ? (
              <div className={styles.loadingSpinner}>
                <div className={styles.spinner}></div>
                <span>Saving...</span>
              </div>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
                </svg>
                Save Settings
              </>
            )}
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Settings;
